import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { StationBand, StationSidePanel } from "./StationPanel";
import LanguageSwitcher from "./LanguageSwitcher";
import { PartnerContext } from "../../context/PartnerContext";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../constants/storeUrls";

const LOGOS = {
  QOVOLTIS: { src: "/logo_qovoltis_header.svg", alt: "Qovoltis" },
  SYDER: { src: "/logo_Syder.svg", alt: "Rhône Syder" },
};

const STEPS = ["steps.connector", "steps.email", "steps.payment"];

// Étapes de la version web : « Prise · E-mail · Paiement ».
function Stepper({ step }) {
  const { t } = useTranslation();

  return (
    <ol className="flex flex-1 items-center gap-2.5" aria-label={t(STEPS[step - 1])}>
      {STEPS.map((label, index) => {
        const number = index + 1;
        const done = number < step;
        const current = number === step;
        return (
          <React.Fragment key={label}>
            <li className="flex shrink-0 items-center gap-2" aria-current={current ? "step" : undefined}>
              <span
                className={`h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold ${
                  done || current ? "bg-qv-brand text-white" : "bg-qv-brand-100 text-qv-brand-900"
                }`}
              >
                {done ? <Icon name="check-read-linear" size={16} /> : number}
              </span>
              <span
                className={`text-sm ${
                  current ? "font-bold text-qv-brand" : done ? "font-semibold text-qv-ink" : "font-medium text-qv-muted"
                }`}
              >
                {t(label)}
              </span>
            </li>
            {number < STEPS.length && (
              <span
                className={`h-0.5 min-w-3 flex-1 rounded-sm ${number < step ? "bg-qv-brand" : "bg-qv-line"}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function BackButton({ onBack }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onBack}
      aria-label={t("common.back")}
      className="h-10 w-10 shrink-0 rounded-full border border-qv-line flex items-center justify-center hover:border-qv-brand hover:text-qv-brand"
    >
      <Icon name="alt-arrow-left-linear" size={20} />
    </button>
  );
}

// Gabarit commun des écrans Scan to Charge.
//
// Mobile (< 768 px), maquette mobile : en-tête (retour, logo, action à droite),
// barre d'étapes, contenu, actions collées en bas d'écran.
//
// Web (≥ 768 px), maquette « Version web » : en-tête pleine largeur (logo, langue),
// carte de 560 px avec retour et étapes « Prise · E-mail · Paiement », actions
// dans la carte, pied de page avec les stores. Le panneau de la borne (`panel`,
// voir utils/stationPanel) se place en bandeau au-dessus de la carte sur tablette,
// puis à côté à partir de 1280 px.
export default function ScreenLayout({ onBack, headerRight, webHeaderRight, step, panel, footer, children }) {
  const { partner } = useContext(PartnerContext) || {};
  const logo = LOGOS[partner] || LOGOS.QOVOLTIS;
  const logoImg = <img src={logo.src} alt={logo.alt} className="h-11" />;

  return (
    <div className="min-h-[100dvh] flex flex-col bg-white md:bg-qv-canvas font-montserrat text-qv-ink">
      <header className="hidden md:flex h-[72px] shrink-0 items-center justify-between border-b border-qv-line bg-white px-12">
        {logoImg}
        {/* Le choix de la langue reste accessible sur tous les écrans web. */}
        {webHeaderRight || <LanguageSwitcher />}
      </header>

      <div className="flex-1 flex flex-col md:flex-row md:flex-wrap md:content-start md:items-start md:justify-center md:gap-x-8 md:gap-y-6 md:px-8 md:py-14">
        {panel && <StationBand panel={panel} className="hidden md:flex xl:hidden" />}

        <div className="relative w-full flex-1 flex flex-col md:w-[560px] md:flex-none md:shrink-0 md:rounded-2xl md:border md:border-qv-line md:bg-white md:p-10 md:shadow-qv-screen">
          <header className="md:hidden h-16 shrink-0 flex items-center justify-between px-5">
            {headerRight && !onBack ? (
              <>
                {logoImg}
                {headerRight}
              </>
            ) : (
              <>
                {onBack ? <BackButton onBack={onBack} /> : <span className="w-10" />}
                {logoImg}
                {headerRight || <span className="w-10" />}
              </>
            )}
          </header>

          {(onBack || step) && (
            <div className="hidden md:flex items-center gap-4 mb-8">
              {onBack && <BackButton onBack={onBack} />}
              {step && <Stepper step={step} />}
            </div>
          )}

          <main className="flex-1 flex flex-col px-6 pt-2 pb-6 md:p-0">
            {step && (
              <div className="md:hidden flex gap-1.5 mb-5" aria-hidden="true">
                {STEPS.map((label, i) => (
                  <span key={label} className={`flex-1 h-1 rounded ${i < step ? "bg-qv-brand" : "bg-qv-line"}`} />
                ))}
              </div>
            )}
            {children}
          </main>

          {footer && (
            <footer className="sticky bottom-0 px-6 pt-4 pb-6 flex flex-col gap-2.5 bg-gradient-to-t from-white from-85% to-white/0 md:static md:bg-none md:p-0 md:pt-7">
              {footer}
            </footer>
          )}
        </div>

        {panel && <StationSidePanel panel={panel} className="hidden xl:block" />}
      </div>

      <footer className="hidden md:flex h-20 shrink-0 items-center justify-center gap-3 border-t border-qv-line bg-white">
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
          <img src="/app-store-badge.svg" alt="App Store" className="h-10" />
        </a>
        <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
          <img src="/google-play-badge.svg" alt="Google Play" className="h-10" />
        </a>
      </footer>
    </div>
  );
}
